import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { fetchClasses } from '../../api/classes';
import { fetchTeacherProfile } from '../../api/teacherProfile';
import { fetchTemplates } from '../../api/templates';
import client from '../../api/client';
import styles from './LessonWizardPage.module.css';
import StandardsPicker from './StandardsPicker';
import TemplateCard from '../Templates/TemplateCard';
import { addLessonStandard } from '../../api/standards';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SUBJECTS = ['Math', 'ELA', 'Science', 'Social Studies', 'Other'];
const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

export default function LessonWizardPage() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [scheduledDate, setScheduledDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Quick lesson mode (no class)
  const [quickMode, setQuickMode] = useState(false);
  const [subject, setSubject] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [lessonTopic, setLessonTopic] = useState('');
  const [profileGrades, setProfileGrades] = useState([]);
  const [showAllGrades, setShowAllGrades] = useState(false);
  const [profileSubjects, setProfileSubjects] = useState([]);
  const [lessonDuration, setLessonDuration] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [classResult, profile, templatesResult] = await Promise.allSettled([
          fetchClasses(),
          fetchTeacherProfile(),
          fetchTemplates(),
        ]);
        if (classResult.status === 'fulfilled') {
          const data = classResult.value;
          setClasses(Array.isArray(data) ? data : data.data || data.classes || []);
        }
        if (profile.status === 'fulfilled' && profile.value) {
          const p = profile.value.profile || profile.value;
          setProfileGrades(p.gradesJson || []);
          setProfileSubjects(p.subjectsJson || []);
          if (p.lessonDuration) setLessonDuration(p.lessonDuration);
        }
        if (templatesResult.status === 'fulfilled' && templatesResult.value) {
          const tpls = templatesResult.value.data || templatesResult.value;
          if (Array.isArray(tpls) && tpls.length > 0) {
            setTemplates(tpls);
            setSelectedTemplate(tpls[0]);
          }
        }
      } catch {
        // Non-critical
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleClassSelect = async (cls) => {
    setSelectedClass(cls);
    setSelectedUnit(null);
    setUnits([]);
    setSelectedStandards([]);
    setLessonTopic('');
    // Prefill subject/grade from the class
    setSubject(cls.subject || '');
    setGradeLevel(String(cls.gradeLevel || ''));
    try {
      const res = await client.get(`/api/classes/${cls.id}`, { headers: getAuthHeaders() });
      const classData = res.data.class || res.data;
      setUnits(classData.units || []);
    } catch {
      // Class may not have units
    }
  };

  const enterQuickMode = () => {
    setQuickMode(true);
    setSelectedClass(null);
    setSelectedUnit(null);
    // Pre-select first profile subject/grade if available
    if (profileSubjects.length === 1) setSubject(profileSubjects[0]);
    if (profileGrades.length === 1) setGradeLevel(profileGrades[0]);
  };

  const exitQuickMode = () => {
    setQuickMode(false);
    setSubject('');
    setGradeLevel('');
    setSelectedStandards([]);
    setLessonTopic('');
    setShowAllGrades(false);
  };

  const handleAddStandard = (standard) => {
    setSelectedStandards(prev => [...prev, standard]);
  };

  const handleRemoveStandard = (standard) => {
    setSelectedStandards(prev => prev.filter(s => s.id !== standard.id));
  };

  const handleCreate = async () => {
    setCreating(true);
    setError('');
    try {
      const effectiveSubject = quickMode ? subject : selectedClass.subject;
      const effectiveGrade = quickMode ? gradeLevel : String(selectedClass.gradeLevel);
      const effectiveDuration = quickMode ? lessonDuration : (selectedClass.mathMinutes || lessonDuration);
      const lessonTitle = quickMode
        ? 'New Lesson'
        : selectedUnit ? `${selectedUnit.title} Lesson` : 'New Lesson';

      const payload = {
        templateVersionId: selectedTemplate?.id || undefined,
        sections: {
          title: lessonTitle,
          subject: effectiveSubject,
          grade_level: effectiveGrade || '',
          duration: effectiveDuration ? String(effectiveDuration) : '',
          ...(lessonTopic.trim() ? { topic: lessonTopic.trim() } : {}),
        },
        scheduledDate: scheduledDate || undefined,
        classId: quickMode ? undefined : selectedClass.id,
        unitId: quickMode ? undefined : (selectedUnit?.id || undefined),
      };

      const res = await client.post('/api/lessons', payload, { headers: getAuthHeaders() });
      const lessonId = res.data.data?.id || res.data.lesson?.id || res.data.id;
      // Link selected standards to the new lesson
      await Promise.allSettled(
        selectedStandards.map(std => addLessonStandard(lessonId, { standardId: std.id }))
      );

      // Pass context to editor via route state
      const standardCodes = selectedStandards.map(s => s.standardCode);
      navigate(`/lessons/${lessonId}/edit`, {
        state: {
          standardCodes,
          lessonTopic: lessonTopic.trim(),
          subject: effectiveSubject,
          gradeLevel: effectiveGrade,
          lessonDuration: effectiveDuration,
          scheduledDate: scheduledDate || null,
          templateVersionId: selectedTemplate?.id || null,
          templatePlaceholders: selectedTemplate?.placeholders || null,
          templateName: selectedTemplate?.name || null,
        }
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create lesson');
      setCreating(false);
    }
  };

  const canCreate = quickMode
    ? !!subject && !!gradeLevel && (selectedStandards.length > 0 || lessonTopic.trim().length > 0)
    : !!selectedClass && (selectedStandards.length > 0 || lessonTopic.trim().length > 0);

  if (loading) return <div className={styles.loading}>Loading...</div>;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>Plan a New Lesson</h1>
        <p className={styles.subtitle}>
          {quickMode
            ? 'Pick a subject and grade to jump right in.'
            : classes.length > 0
              ? 'Select a class or start a quick lesson.'
              : 'Start a quick lesson, or create a class first.'}
        </p>

        {error && <div className={styles.error}>{error}</div>}

        {/* Template Selection */}
        {templates.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Choose a Template</h3>
            <div className={styles.templateGrid}>
              {templates.map((t) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  isSelected={selectedTemplate?.id === t.id}
                  onSelect={setSelectedTemplate}
                />
              ))}
            </div>
            <Link to="/templates/new" className={styles.createTemplateLink}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create New Template
            </Link>
          </div>
        )}

        {/* Quick Mode: subject/grade selection */}
        {quickMode && (
          <>
            <div className={styles.section}>
              <div className={styles.quickModeHeader}>
                <h2 className={styles.sectionTitle}>Subject</h2>
                <button className={styles.changeBtn} onClick={exitQuickMode}>
                  {classes.length > 0 ? 'Pick a class instead' : 'Back'}
                </button>
              </div>
              <div className={styles.unitList}>
                {SUBJECTS.map(s => (
                  <button
                    key={s}
                    className={`${styles.unitBtn} ${subject === s ? styles.unitBtnActive : ''}`}
                    onClick={() => setSubject(subject === s ? '' : s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Grade</h2>
              <div className={styles.unitList}>
                {(profileGrades.length > 0 && !showAllGrades ? profileGrades : GRADES).map(g => (
                  <button
                    key={g}
                    className={`${styles.unitBtn} ${gradeLevel === g ? styles.unitBtnActive : ''}`}
                    onClick={() => setGradeLevel(gradeLevel === g ? '' : g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
              {profileGrades.length > 0 && (
                <button
                  className={styles.toggleLink}
                  onClick={() => setShowAllGrades(prev => !prev)}
                >
                  {showAllGrades ? 'Show my grades' : 'Show all grades'}
                </button>
              )}
            </div>

            {/* Standards & Topic — visible once subject and grade selected */}
            {subject && gradeLevel && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>What should this lesson cover?</h2>
                <p className={styles.sectionHint}>Select standards and/or describe your lesson topic.</p>
                <StandardsPicker
                  subject={subject}
                  gradeLevel={gradeLevel}
                  selectedStandards={selectedStandards}
                  onAdd={handleAddStandard}
                  onRemove={handleRemoveStandard}
                />
                <textarea
                  value={lessonTopic}
                  onChange={(e) => setLessonTopic(e.target.value)}
                  placeholder="Other learning objectives (e.g., 'comparing fractions using visual models')"
                  className={styles.topicInput}
                  rows={3}
                />
              </div>
            )}

            {/* Date */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Schedule Date (optional)</h2>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className={styles.dateInput}
              />
            </div>

            <button
              className={styles.createBtn}
              onClick={handleCreate}
              disabled={creating || !canCreate}
            >
              {creating ? 'Creating...' : 'Continue to Lesson Planning'}
            </button>
          </>
        )}

        {/* Class selection mode */}
        {!quickMode && !selectedClass && (
          <>
            {/* Quick lesson option */}
            <button className={styles.quickBtn} onClick={enterQuickMode}>
              Quick Lesson — skip class selection
            </button>

            {classes.length > 0 && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Or choose a class</h2>
                <div className={styles.cardGrid}>
                  {classes.map(cls => (
                    <button
                      key={cls.id}
                      className={styles.classCard}
                      onClick={() => handleClassSelect(cls)}
                    >
                      <span className={styles.classSubject}>{cls.subject}</span>
                      <span className={styles.classLabel}>{cls.name}</span>
                      <span className={styles.classGrade}>Grade {cls.gradeLevel}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Selected class + options */}
        {!quickMode && selectedClass && (
          <>
            <div className={styles.selectedCard}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span className={styles.classSubject}>{selectedClass.subject}</span>
                <span className={styles.classLabel}>{selectedClass.name}</span>
              </div>
              <button className={styles.changeBtn} onClick={() => { setSelectedClass(null); setSelectedUnit(null); setSelectedStandards([]); setLessonTopic(''); setSubject(''); setGradeLevel(''); }}>
                Change
              </button>
            </div>

            {/* Unit Selection */}
            {units.length > 0 && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Choose a Unit (optional)</h2>
                <div className={styles.unitList}>
                  {units.map(unit => (
                    <button
                      key={unit.id}
                      className={`${styles.unitBtn} ${selectedUnit?.id === unit.id ? styles.unitBtnActive : ''}`}
                      onClick={() => setSelectedUnit(selectedUnit?.id === unit.id ? null : unit)}
                    >
                      {unit.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Standards & Topic */}
            {subject && gradeLevel && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>What should this lesson cover?</h2>
                <p className={styles.sectionHint}>Select standards and/or describe your lesson topic.</p>
                <StandardsPicker
                  subject={subject}
                  gradeLevel={gradeLevel}
                  selectedStandards={selectedStandards}
                  onAdd={handleAddStandard}
                  onRemove={handleRemoveStandard}
                />
                <textarea
                  value={lessonTopic}
                  onChange={(e) => setLessonTopic(e.target.value)}
                  placeholder="Other learning objectives (e.g., 'comparing fractions using visual models')"
                  className={styles.topicInput}
                  rows={3}
                />
              </div>
            )}

            {/* Date */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Schedule Date (optional)</h2>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                className={styles.dateInput}
              />
            </div>

            {/* Create */}
            <button
              className={styles.createBtn}
              onClick={handleCreate}
              disabled={creating || !canCreate}
            >
              {creating ? 'Creating...' : 'Continue to Lesson Planning'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
